/**
 * Per-language logo for the public booking website (TKT-20261102-0007).
 *
 * Founder decision 2026-11-02: the booking site shows the Arabic logo on
 * Arabic pages and the English logo on English pages. Booking website only -
 * PDFs and emails keep the single tenant logo, the same split the booking
 * colours make.
 *
 * Rule, per language:
 *   that language's booking logo -> the other language's booking logo -> tenant logo
 * so a tenant that uploads only one booking logo gets it on both languages, and
 * a tenant that uploads none sees exactly what it saw before (`logo_file_id`).
 */

import { getStorageUrl } from '@/lib/storage-url'

/** The three logo file ids a tenant row carries. */
export interface TenantLogoFileIds {
  logo_file_id: string | null
  booking_logo_ar_file_id?: string | null
  booking_logo_en_file_id?: string | null
}

/** Logo URLs already resolved for each booking-site language. */
export interface BookingLogos {
  /** Logo for Arabic pages (null only when the tenant has no logo at all). */
  tenantLogoAr: string | null
  /** Logo for English pages (null only when the tenant has no logo at all). */
  tenantLogoEn: string | null
}

/** Resolve the Arabic and English booking logos from a tenant row, with fallbacks applied. */
export function resolveBookingLogos(tenant: TenantLogoFileIds): BookingLogos {
  const ar = tenant.booking_logo_ar_file_id || null
  const en = tenant.booking_logo_en_file_id || null
  const fallback = tenant.logo_file_id || null
  return {
    tenantLogoAr: getStorageUrl(ar ?? en ?? fallback),
    tenantLogoEn: getStorageUrl(en ?? ar ?? fallback),
  }
}

/** True for any Arabic locale tag (`ar`, `ar-SA`, ...). */
function isArabicLocale(locale: string | null | undefined): boolean {
  return (locale ?? '').toLowerCase().split('-')[0] === 'ar'
}

/** Pick the logo URL for the page's language. Any non-Arabic locale gets the English logo. */
export function pickBookingLogo(
  logos: BookingLogos | null | undefined,
  locale: string | null | undefined,
): string | null {
  if (!logos) return null
  return isArabicLocale(locale) ? logos.tenantLogoAr : logos.tenantLogoEn
}
